import { useEffect, useRef, useState } from 'react';

const VERSION_URL = '/version.json';
const SSE_URL = '/api/version/stream';
const POLL_INTERVAL_VISIBLE_MS = 20 * 1000;   // active polling fallback
const POLL_INTERVAL_HIDDEN_MS = 0;            // 0 = paused when tab hidden
const SSE_RETRY_DELAY_MS = 5000;              // backoff when SSE drops
const FORCE_DIALOG_AFTER_MS = 10 * 60 * 1000; // 10 min "grace" before escalation
const BROADCAST_CHANNEL = 'fabtraque-version-check';

/** One-shot fetch of /version.json. Returns the version string or null on error. */
async function fetchCurrentVersion() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version != null ? String(data.version) : null;
  } catch {
    return null;
  }
}

/**
 * Detect new deployments and decide when to nudge the user.
 *
 * Detection sources (first-available wins):
 *   1. Server-Sent Events on /api/version/stream — instant: a deploy kills the
 *      old container, all SSE connections drop, the browser reconnects, the
 *      new backend sends its current version. Latency ~100 ms.
 *   2. Polling /version.json every 20 s as a fallback (when SSE is blocked
 *      by a corp proxy, errors out, or is unsupported). Paused while the tab
 *      is hidden to save server load.
 *   3. Re-check on visibilitychange + window focus → instant catch-up when
 *      the user comes back from another tab / app.
 *
 * UX escalation:
 *   - `newVersionAvailable` flips first → use it for a soft snackbar
 *   - `forceDialog` flips 10 minutes later if the user keeps ignoring
 *
 * Multi-tab sync:
 *   - When this tab detects a new version, BroadcastChannel notifies every
 *     other tab so they all show the popup at the same instant — no need to
 *     wait for each tab's own SSE/poll cycle.
 */
export function useNewVersionCheck() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [forceDialog, setForceDialog] = useState(false);

  const initialVersionRef = useRef(null);
  const announcedRef = useRef(false);
  const escalationTimerRef = useRef(null);
  const broadcastRef = useRef(null);
  const pollTimerRef = useRef(null);
  const eventSourceRef = useRef(null);
  const sseRetryTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    /* Set up the cross-tab channel so a detection in one tab fires the popup
       in all the others (no waiting on each tab's own check cycle). */
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        broadcastRef.current = new BroadcastChannel(BROADCAST_CHANNEL);
        broadcastRef.current.onmessage = (ev) => {
          if (ev?.data?.type === 'new-version' && !announcedRef.current) {
            announce();
          }
        };
      } catch { /* not supported — silent */ }
    }

    /** Mark detection (idempotent), schedule the 10-min escalation, notify peers. */
    const announce = () => {
      if (announcedRef.current) return;
      announcedRef.current = true;
      setNewVersionAvailable(true);
      // Tell every other tab so they don't have to wait for their own check
      try { broadcastRef.current?.postMessage({ type: 'new-version' }); } catch {}
      // Escalation: if the user keeps ignoring the snackbar, after 10 min
      // upgrade to a forced dialog. Long enough for someone to finish a
      // form, short enough to not let stale state cause real bugs.
      if (!escalationTimerRef.current) {
        escalationTimerRef.current = setTimeout(() => setForceDialog(true), FORCE_DIALOG_AFTER_MS);
      }
      // Stop polling / close SSE — we already know everything we need.
      stopPolling();
      stopSse();
    };

    /** Compare a freshly-fetched version against the initial one. */
    const handleVersion = (version) => {
      if (cancelled || version == null) return;
      if (initialVersionRef.current == null) {
        initialVersionRef.current = version;
        return;
      }
      if (version !== initialVersionRef.current) announce();
    };

    /* SSE primary path. We treat any non-`version` event (or no event at
       all) as benign — only the explicit `version` event drives detection. */
    const startSse = () => {
      if (typeof EventSource === 'undefined') return;
      if (eventSourceRef.current) return;
      try {
        const es = new EventSource(SSE_URL);
        eventSourceRef.current = es;
        es.addEventListener('version', (ev) => {
          try {
            const data = JSON.parse(ev.data);
            handleVersion(data?.version != null ? String(data.version) : null);
          } catch { /* malformed payload — ignore */ }
        });
        es.onerror = () => {
          // Close and clear the dead handle so the retry below (and the
          // focus/visibility restart) can actually build a fresh EventSource —
          // otherwise startSse() early-returns on the stale ref and the primary
          // channel stays dead for the tab's lifetime. Taking over reconnection
          // ourselves (instead of the browser's ~3s auto-retry) also stops the
          // reconnect storm that used to reset the poll interval every few ms.
          try { es.close(); } catch { /* already closed */ }
          if (eventSourceRef.current === es) eventSourceRef.current = null;
          if (!cancelled && !announcedRef.current) {
            schedulePoll();
            if (!sseRetryTimerRef.current) {
              sseRetryTimerRef.current = setTimeout(() => {
                sseRetryTimerRef.current = null;
                if (!cancelled && !announcedRef.current) startSse();
              }, SSE_RETRY_DELAY_MS);
            }
          }
        };
      } catch { /* unsupported / blocked */ }
    };
    const stopSse = () => {
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch {}
        eventSourceRef.current = null;
      }
      if (sseRetryTimerRef.current) {
        clearTimeout(sseRetryTimerRef.current);
        sseRetryTimerRef.current = null;
      }
    };

    /* Polling fallback — only runs when the tab is visible. */
    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    const schedulePoll = () => {
      // Idempotent: if a poll interval is already live, keep it. The SSE
      // onerror path calls this on every reconnect attempt (~3s apart); without
      // this guard each call cleared and recreated the 20s interval, so the
      // poll callback never actually fired while SSE was down.
      if (pollTimerRef.current) return;
      const interval = document.visibilityState === 'hidden'
        ? POLL_INTERVAL_HIDDEN_MS
        : POLL_INTERVAL_VISIBLE_MS;
      if (interval <= 0) return;
      pollTimerRef.current = setInterval(() => {
        if (announcedRef.current) { stopPolling(); return; }
        if (document.visibilityState === 'hidden') return;
        fetchCurrentVersion().then(handleVersion);
      }, interval);
    };

    /** Re-check immediately when the user comes back to the tab. */
    const onComeBack = () => {
      if (cancelled || announcedRef.current) return;
      fetchCurrentVersion().then(handleVersion);
      // Restart SSE if it dropped while hidden
      if (!eventSourceRef.current) startSse();
      schedulePoll();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onComeBack();
      else stopPolling();
    };

    // Initial fetch — populates initialVersion and primes everything.
    fetchCurrentVersion().then(handleVersion);
    startSse();
    schedulePoll();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onComeBack);

    return () => {
      cancelled = true;
      stopPolling();
      stopSse();
      if (escalationTimerRef.current) {
        clearTimeout(escalationTimerRef.current);
        escalationTimerRef.current = null;
      }
      if (broadcastRef.current) {
        try { broadcastRef.current.close(); } catch {}
        broadcastRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onComeBack);
    };
  }, []);

  return { newVersionAvailable, forceDialog };
}
